import { faCheck, faCircleXmark, faChevronDown, faChevronLeft, faDoorOpen, faListOl, faTimes, faTriangleExclamation, faStar } from "@fortawesome/free-solid-svg-icons";
import { faCircleCheck, faUser } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../../Components/Header/Header";
import "../LeaderboardDebugSession/LeaderboardDebugSession.css";
import "../LeaderboardTestingSession/LeaderboardTestingSession.css";
import "./LeaderboardHTTraining.css";

// Teacher-facing results for HighTension interactive-training sessions.
// Mirrors LeaderboardTestingSession but shows the server-computed score and the
// categorized findings (critical / major / minor) per student.
const LeaderboardHTTraining = ({ data, socket, sessionName } : { data: any, socket: any, sessionName?: string }) => {
    const params = useParams();
    const navigate = useNavigate();
    const sessionId = params?.sessionId;
    const [results, setResults] = useState<Array<any>>([]);
    const [cookies] = useCookies();
    const location = useLocation();
    const realSessionName = location?.state?.sessionName || sessionName;

    const [sessionFinished, setSessionFinished] = useState<boolean>(false);
    const [activeUser, setActiveUser] = useState<any | null>(null);

    // Owner = the table the session lives in (self-created student sessions are
    // owned by the student, passed via navigation state).
    const ownerId = location?.state?.ownerId || window.history.state?.ownerId;
    const teacherId = ownerId || (data.userData.type === "teacher" ? data.userData.userId : data.userData.teacherId);

    // Teachers monitor their students, so the owner's own run is never shown to
    // anyone else. The owner does see it — a teacher who joined and finished the
    // scenario gets their own statistics too, listed like a student row.
    const isTeacherView = data.userData.type === "teacher";
    const myId = data.userData.userId;
    const getLeaderboard = async () => {
        try {
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-httraining/get-results-of-session-httraining/${teacherId}/${sessionId}`, { headers: { "Content-Type": "application/json" } });
            const response = await request.json();
            if (response.success) setResults((response.results || []).filter((u: any) => {
                if (!u) return false;
                if (u.userId !== teacherId) return true;    // ordinary participant
                if (u.userId !== myId) return false;        // someone else's owner row
                // Own row: a teacher's appears once they finished their own run
                // (before that there is nothing to show but zeros).
                return !isTeacherView || !!u.finished;
            }));
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        (async () => {
            try {
                const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-httraining/check-id-httraining/${teacherId}/${sessionId}`, { headers: { "Content-Type": "application/json" } });
                const response = await request.json();
                setSessionFinished(response.results?.finished);
                await getLeaderboard();
            } catch (e) { /* ignore */ }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        socket.on("update-results-httraining", () => { getLeaderboard(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const finishSessionTeacher = async () => {
        if (data.userData.type !== "teacher") return;
        try {
            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/session-httraining/finish-session-httraining`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ teacherId: data.userData.userId, sessionId }),
            });
            const response = await request.json();
            if (response.success) socket.emit("session-httraining-finished", sessionId, data.userData.userId);
            window.location.reload();
        } catch (e) { /* ignore */ }
    };

    const findingGroups: { key: "critical" | "major" | "minor", title: string }[] = [
        { key: "critical", title: cookies?.language === "en" ? "Critical errors" : "Erori grave" },
        { key: "major", title: cookies?.language === "en" ? "Mistakes" : "Greseli" },
        { key: "minor", title: cookies?.language === "en" ? "Warnings" : "Avertismente" },
    ];

    return (
        <div className="content">
            <Header data={data} socket={socket} />
            <div className="htl-back-btn" onClick={() => navigate("/high-tension/training")} title="Inapoi">
                <FontAwesomeIcon icon={faChevronLeft} className="back-icon" />
            </div>
            {realSessionName ? <h2 className="title" style={{ textAlign: "center" }}>{realSessionName}</h2> : null}

            {data.userData.type === "teacher" ? (
                <div className="join-session-container" onClick={() => {
                    window.history.replaceState({ sessionName: realSessionName }, "");
                    setTimeout(() => window.location.replace(`/session-httraining/${sessionId}/1`), 100);
                }}>
                    <FontAwesomeIcon icon={faDoorOpen} className="icon" />
                    <h2>{cookies?.language === "en" ? "Join session" : "Intra in sesiune"}</h2>
                </div>
            ) : null}

            {/* Student rows, styled like the "current sessions" list: one card per
                trainee, click to expand their detailed report below. */}
            <div className="htl-rows">
                {results && results.length ? results.map((result, idx) => {
                    const isActive = JSON.stringify(result) === JSON.stringify(activeUser || "[]");
                    const noScore = result?.score === null || result?.score === undefined;
                    const isMine = result?.userId === myId;
                    return (
                        <div className={`htl-row${isActive ? " active" : ""}${isMine ? " mine" : ""}`} key={idx}
                            onClick={() => result === activeUser ? setActiveUser(null) : setActiveUser(result)}>
                            <div className="htl-index">{idx + 1}</div>

                            <div className="htl-who">
                                <span className="htl-name"><FontAwesomeIcon icon={faUser} className="htl-name-icon" />{result.name}</span>
                                <span className="htl-tags">
                                    {isMine ? (
                                        <span className="htl-tag mine">
                                            {cookies?.language === "en" ? "You" : "Tu"}
                                        </span>
                                    ) : null}
                                    <span className={`htl-tag ${result?.entered ? "on" : "off"}`}>
                                        <FontAwesomeIcon icon={result?.entered ? faCheck : faTimes} />
                                        {cookies?.language === "en" ? "Entered" : "Intrat"}
                                    </span>
                                    <span className={`htl-tag ${result?.finished ? "on" : "off"}`}>
                                        <FontAwesomeIcon icon={result?.finished ? faCheck : faTimes} />
                                        {cookies?.language === "en" ? "Finished" : "Terminat"}
                                    </span>
                                </span>
                            </div>

                            <div className="htl-stats">
                                <div className="htl-stat" data-tip={cookies?.language === "en" ? "Maneuvers" : "Manevre"}>
                                    <FontAwesomeIcon icon={faCircleCheck} className="htl-stat-icon ok" />
                                    <b>{result?.correct?.length || 0}</b>
                                </div>
                                <div className="htl-stat" data-tip={cookies?.language === "en" ? "Warnings" : "Avertizari"}>
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="htl-stat-icon warn" />
                                    <b>{result?.warning?.length || 0}</b>
                                </div>
                                <div className="htl-stat" data-tip={cookies?.language === "en" ? "Wrong" : "Gresite"}>
                                    <FontAwesomeIcon icon={faCircleXmark} className="htl-stat-icon bad" />
                                    <b>{result?.wrong?.length || 0}</b>
                                </div>
                                <div className={`htl-score${noScore ? " na" : ""}`} data-tip={cookies?.language === "en" ? "Score" : "Scor"}>
                                    <FontAwesomeIcon icon={faStar} className="htl-score-icon" />
                                    <b>{noScore ? "N/A" : result.score}</b>
                                    {noScore ? null : <span>/ 100</span>}
                                </div>
                                <FontAwesomeIcon icon={faChevronDown} className="htl-chevron" />
                            </div>
                        </div>
                    );
                }) : (
                    <div className="htl-empty">
                        <FontAwesomeIcon icon={faListOl} />
                        <p>{cookies?.language === "en" ? "No students in this session yet." : "Niciun elev in aceasta sesiune."}</p>
                    </div>
                )}
            </div>

            {activeUser !== null ? (
                <div className="ht-results">
                    <div className="ht-head">
                        <div className="ht-avatar">{(activeUser?.name || "?").trim().charAt(0).toUpperCase()}</div>
                        <div className="ht-who">
                            <h2 className="ht-name">{activeUser?.name}</h2>
                            <p className="ht-sub">{cookies?.language === "en" ? "Session results" : "Rezultatele sesiunii"}</p>
                        </div>
                        <div className="ht-score-pill">
                            <b>{activeUser?.score === null || activeUser?.score === undefined ? "N/A" : activeUser.score}</b>
                            {activeUser?.score === null || activeUser?.score === undefined ? null : <span>/ 100</span>}
                        </div>
                    </div>

                    <div className="ht-grid">
                        <div className="ht-card">
                            <h3 className="ht-card-title">
                                <span className="ht-t-icon"><FontAwesomeIcon icon={faCircleCheck} /></span>
                                {cookies?.language === "en" ? `${activeUser?.name}'s maneuvers` : `Manevrele lui ${activeUser?.name}`}
                            </h3>
                            {(activeUser.logs || []).length ? (activeUser.logs || []).map((log: any, idx: number) => (
                                <div className="ht-step" key={idx}>
                                    <span className="ht-num">{idx + 1}</span>
                                    <FontAwesomeIcon icon={log.type === "wrong" ? faCircleXmark : log.type === "warning" ? faTriangleExclamation : faCircleCheck} className={`item-icon ${log.type}`} />
                                    <p>{log.msg}{log.cell ? <span className="ht-cell"> — {log.cell}</span> : ""}</p>
                                </div>
                            )) : <div className="ht-empty">{cookies?.language === "en" ? "No maneuvers recorded" : "Nicio manevra inregistrata"}</div>}
                        </div>

                        <div className="ht-card">
                            <h3 className="ht-card-title">
                                <span className="ht-t-icon"><FontAwesomeIcon icon={faStar} /></span>
                                {cookies?.language === "en" ? "Evaluation" : "Evaluare"}
                            </h3>
                            <div className="ht-stat">
                                <span className="ht-stat-label">{cookies?.language === "en" ? "Score" : "Scor"}</span>
                                <span className="ht-stat-value">{activeUser?.score === null || activeUser?.score === undefined ? "N/A" : `${activeUser.score} / 100`}</span>
                            </div>
                            <div className="ht-stat">
                                <span className="ht-stat-label">{cookies?.language === "en" ? "Reached target state" : "A atins starea ceruta"}</span>
                                {activeUser?.finalStateMatches === null || activeUser?.finalStateMatches === undefined
                                    ? <span className="ht-stat-value">N/A</span>
                                    : <span className={`ht-badge ${activeUser.finalStateMatches ? "ok" : "no"}`}>{activeUser.finalStateMatches ? "DA" : "NU"}</span>}
                            </div>
                            {activeUser?.finalStateMatches === false && (activeUser?.missing || []).length > 0 ? (
                                <div className="ht-findings major">
                                    <h4>{cookies?.language === "en" ? "What was not done" : "Ce nu a facut"} ({activeUser.missing.length})</h4>
                                    {activeUser.missing.map((m: string, i: number) => (
                                        <div className="ht-finding" key={i}>{m}</div>
                                    ))}
                                </div>
                            ) : null}
                            {activeUser?.findings ? findingGroups.map((g) => {
                                const items = activeUser.findings[g.key] || [];
                                if (!items.length) return null;
                                return (
                                    <div className={`ht-findings ${g.key}`} key={g.key}>
                                        <h4>{g.title} ({items.length})</h4>
                                        {items.map((it: any, i: number) => (
                                            <div className="ht-finding" key={i}>{it.msg}</div>
                                        ))}
                                    </div>
                                );
                            }) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {data.userData.type === "teacher" && !sessionFinished ? (
                <div className="start-session-button" onClick={async () => await finishSessionTeacher()}>
                    <p>{cookies?.language === "en" ? "Finish session" : "Incheie sesiunea"}</p>
                </div>
            ) : null}
        </div>
    );
};

export default LeaderboardHTTraining;
